import { Button, Group, Modal, Stack } from '@mantine/core';
import { useSelector } from 'react-redux';
import Text from '@ui/components/Text/Text';
import { getMemberAddOn } from 'src/selectors/addOns';
import AddOn from 'src/entities/addOn';
import useWorkspace from '../../hooks/useWorkspace';
import useAssignSeatToNonMemberMutation from '../../queries/useAssignSeatToNonMemberMutation';
import { NonMemberJSON } from '../../types';

type Props = {
	workspaceID: number;
	nonMember: NonMemberJSON;
	onClose: () => void;
	onSuccess: () => void;
};

const AssignSeatModal = ( { workspaceID, nonMember, onClose, onSuccess }: Props ) => {
	const workspace = useWorkspace( workspaceID );
	const memberAddOn: AddOn | undefined = useSelector( getMemberAddOn );

	const { mutate, isLoading, error } = useAssignSeatToNonMemberMutation( {
		workspaceID,
		onSuccess: () => { onSuccess(); onClose(); }
	} );

	if ( !workspace ) return null;

	const hasAvailableSeat = workspace.remainingSeats > 0;
	if ( !hasAvailableSeat && !memberAddOn ) return null;

	const isAnnual = workspace.hasYearlySubscriptionInterval;
	const monthlyUnitPrice = memberAddOn
		? isAnnual ? memberAddOn.annualPriceCost / 12 : memberAddOn.monthlyPriceCost
		: 0;
	const formattedMonthlyCost = memberAddOn ? `$${monthlyUnitPrice.toFixed( 2 )}` : '';
	const formattedAnnualCost = isAnnual && memberAddOn ? `$${memberAddOn.annualPriceCost.toFixed( 2 )}/year` : '';

	return (
		<Modal
			opened
			onClose={onClose}
			title={(
				<Text
					variant="h5"
					style={{ color: 'var(--color-text-primary)', letterSpacing: 0, lineHeight: '22px' }}
				>
					{hasAvailableSeat ? 'Assign a seat' : 'Additional charges will apply'}
				</Text>
			)}
			size={413}
			styles={{
				close: { color: 'var(--color-text-secondary)', width: 20, height: 20, minWidth: 20, minHeight: 20 },
				header: { paddingTop: 15, paddingBottom: 15, paddingLeft: 22, borderBottom: '1px solid var(--color-outline-light)', backgroundColor: 'var(--color-bg-container)' },
				content: { backgroundColor: 'var(--color-bg-container)' },
				body: { padding: 0, backgroundColor: 'var(--color-bg-container)' }
			}}
		>
			<Stack spacing={24} px={20} pt={20} pb={24}>
				<Stack spacing={16}>
					<Text variant="body" style={{ lineHeight: '22px', color: 'var(--color-text-primary)' }}>
						You are about to add <strong>{nonMember.email}</strong> to your workspace.
					</Text>
					{hasAvailableSeat ? (
						<Text variant="body" style={{ lineHeight: '22px', color: 'var(--color-text-secondary)' }}>
							You have an available seat, so no additional charges will apply.
						</Text>
					) : (
						<>
							<Group
								spacing={0}
								position="apart"
								px={12}
								py={10}
								style={{
									border: '1px solid var(--color-outline-light-blue)',
									borderRadius: 4,
									background: 'var(--color-bg-surface-container-blue)'
								}}
							>
								<Stack spacing={2}>
									<Text variant="body-bold" color="blue.5" style={{ lineHeight: '22px' }}>
										Additional charge*
									</Text>
									<Text variant="body" style={{ lineHeight: '22px', color: 'var(--color-text-primary)' }}>
										1 seat x {formattedMonthlyCost}/mo
									</Text>
								</Stack>
								<Stack spacing={2} style={{ textAlign: 'right' }}>
									<Group spacing={2} position="right" align="baseline" noWrap>
										<Text variant="h2" color="blue.5" style={{ lineHeight: '28px', letterSpacing: '-0.38px' }}>
											+{formattedMonthlyCost}
										</Text>
										<Text variant="footnote" style={{ lineHeight: '18px', color: 'var(--color-text-secondary)' }}>
											/mo
										</Text>
									</Group>
									<Text variant="caption-regular" style={{ color: 'var(--color-text-secondary)' }}>{formattedAnnualCost}</Text>
								</Stack>
							</Group>
							<Text variant="caption-regular" style={{ color: 'var(--color-text-secondary)' }} mt={-10}>*Charged now, prorated from today to your next renewal date</Text>
						</>
					)}
					{error && <Text variant="caption" color="red.5">{error}</Text>}
				</Stack>
				<Group position="right" mt={-8} mb={6} spacing={12}>
					<Button
						variant="outline"
						onClick={onClose}
						px={20}
						py={7}
						disabled={isLoading}
						sx={{ 'color': 'var(--color-text-secondary)', 'borderColor': 'var(--color-outline-default)', 'fontSize': 14, 'fontWeight': 600, 'letterSpacing': 0, 'lineHeight': '22px', 'borderRadius': 4, '&:hover': { backgroundColor: 'var(--color-bg-surface-container)' } }}
					>
						Cancel
					</Button>
					<Button
						onClick={() => mutate( nonMember )}
						w={143}
						px={12}
						py={8}
						loading={isLoading}
						color="blue.5"
						sx={{ fontSize: 14, fontWeight: 600, lineHeight: '20px', letterSpacing: 0, borderRadius: 4 }}
					>
						Add Seat
					</Button>
				</Group>
			</Stack>
		</Modal>
	);
};

export default AssignSeatModal;
